import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Award, Bot, CheckCircle2, ClipboardCheck, Clock3, FileText, LogOut, Save, Send, ShieldCheck, Sparkles, Upload } from 'lucide-react';
import { useAuth } from '@/core/hooks';
import { useCompanyProfile } from '@/app/utils/companyProfile';

interface CandidatePortalRecord {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  roleAppliedFor: string;
  branchId?: string;
  availability?: string;
  experienceSummary?: string;
  aiScreeningSummary?: string;
  address?: string;
  postcode?: string;
  dateOfBirth?: string;
  nationalInsuranceNumber?: string;
  desiredHours?: string;
  preferredStartDate?: string;
  hasDrivingLicence?: boolean;
  hasOwnTransport?: boolean;
  applicationSubmittedAt?: string;
  candidateExamOutcome?: string;
  candidateExamScore?: string;
  candidateExamSubmittedAt?: string;
  candidatePortalMessage?: string;
  applicationProfile?: CandidateApplicationProfile | null;
  examLoginExpiresAt?: string;
}

interface CandidateApplicationProfile {
  address?: string;
  postcode?: string;
  dateOfBirth?: string;
  nationalInsuranceNumber?: string;
  desiredHours?: string;
  preferredStartDate?: string;
  hasDrivingLicence?: boolean;
  hasOwnTransport?: boolean;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  referenceOneName?: string;
  referenceOneEmail?: string;
  referenceTwoName?: string;
  referenceTwoEmail?: string;
  rightToWorkDeclaration?: boolean;
  dbsConsent?: boolean;
  gdprConsent?: boolean;
  truthfulDeclaration?: boolean;
}

interface CandidateDocument {
  id: string;
  candidateId: string;
  documentType: string;
  status: string;
  fileName?: string;
  notes?: string;
  createdAt?: string;
}

interface ExamQuestion {
  id: string;
  category: string;
  question: string;
  expectedAnswer: string;
  candidateAnswer: string;
  score: number;
  maxScore: number;
  notes: string;
  critical?: boolean;
  keywords: string[];
}

interface ExamTemplate {
  title: string;
  durationMinutes: number;
  passMark: number;
  maxCriticalErrors: number;
  rules: string[];
  questions: ExamQuestion[];
}

function q(id: string, category: string, question: string, expectedAnswer: string, keywords: string[], critical = false): ExamQuestion {
  return { id, category, question, expectedAnswer, keywords, critical, candidateAnswer: '', score: 0, maxScore: 5, notes: '' };
}

function baseExamForRole(role: string): ExamTemplate {
  const normalized = role.toLowerCase();
  if (normalized.includes('manager') || normalized.includes('deputy')) {
    return {
      title: `${role} AI Governance Exam`,
      durationMinutes: 70,
      passMark: 85,
      maxCriticalErrors: 0,
      rules: ['Answer every question.', 'Critical governance or safeguarding failures require human review.', 'AI recommendation is advisory only.'],
      questions: [
        q('governance-cqc', 'CQC governance', 'How would you evidence that a care service is safe, effective, caring, responsive and well-led?', 'Use audits, incidents, complaints, training, feedback, action plans and evidence review.', ['audit', 'incident', 'training', 'feedback', 'action'], true),
        q('safeguarding-lead', 'Safeguarding', 'A safeguarding allegation is made against a staff member. What must happen first and next?', 'Protect the client, remove immediate risk, escalate, preserve evidence, notify as required and record actions.', ['protect', 'risk', 'escalate', 'evidence', 'record'], true),
        q('data-governance', 'Data protection', 'How should sensitive care records be controlled?', 'Role access, lawful basis, encryption, audit logs, retention and breach process.', ['role', 'lawful', 'encryption', 'audit', 'breach'], true),
        q('staffing-risk', 'Safe staffing', 'How do you know rota capacity is safe?', 'Review client needs, skills, continuity, travel time, training and missed visit/overtime indicators.', ['needs', 'skills', 'travel', 'training', 'missed'], false),
      ],
    };
  }
  if (normalized.includes('coordinator')) {
    return {
      title: `${role} AI Scheduling and Risk Exam`,
      durationMinutes: 55,
      passMark: 78,
      maxCriticalErrors: 0,
      rules: ['Safety overrides cost saving.', 'Rota gaps for high-risk clients must be escalated immediately.', 'AI recommendation is advisory only.'],
      questions: [
        q('rota-gap', 'Rota safety', 'A carer calls sick 30 minutes before a double-up visit. What do you do?', 'Assess risk, find trained cover, inform senior/manager, update appropriate people and document.', ['risk', 'trained', 'manager', 'update', 'document'], true),
        q('skill-match', 'Skill matching', 'What should be checked before assigning a carer?', 'Competence, training, availability, distance, continuity and client preferences.', ['training', 'competence', 'availability', 'distance', 'preference'], false),
        q('late-checkin', 'Visit monitoring', 'A carer has not checked in after the visit start time. What do you do?', 'Contact carer, check telemetry, alert senior, protect continuity and record actions.', ['contact', 'telemetry', 'senior', 'continuity', 'record'], true),
        q('cost-control', 'Cost control', 'How can scheduling reduce cost without reducing safety?', 'Reduce travel, avoid overtime, use nearby trained carers and review underused capacity.', ['travel', 'overtime', 'nearby', 'trained', 'capacity'], false),
      ],
    };
  }
  if (normalized.includes('account') || normalized.includes('contabil')) {
    return {
      title: `${role} AI Payroll and Billing Exam`,
      durationMinutes: 45,
      passMark: 75,
      maxCriticalErrors: 0,
      rules: ['Use verified visit records only.', 'Protect payroll and client financial data.', 'AI recommendation is advisory only.'],
      questions: [
        q('payroll-source', 'Payroll control', 'What source should payroll use for carer hours?', 'Verified check-in/check-out, approved exceptions and authorised overtime rules.', ['verified', 'check-in', 'check-out', 'approved', 'overtime'], true),
        q('billing-source', 'Billing control', 'How should client invoices be generated?', 'From completed verified visits, agreed billable rates, adjustments and audit trail.', ['completed', 'verified', 'rates', 'adjustments', 'audit'], false),
        q('finance-confidentiality', 'Confidentiality', 'Can payroll details be shared with another employee?', 'No, only authorised roles may access payroll and access should be logged.', ['no', 'authorised', 'access', 'logged'], true),
      ],
    };
  }
  if (normalized.includes('technical') || normalized.includes('tehnic')) {
    return {
      title: `${role} AI Security and Integrations Exam`,
      durationMinutes: 50,
      passMark: 80,
      maxCriticalErrors: 0,
      rules: ['Never expose secrets in browser code.', 'Backups and sensitive records must be encrypted.', 'AI recommendation is advisory only.'],
      questions: [
        q('api-keys', 'API key security', 'Where should real API keys be stored and why?', 'Server-side secret storage, encrypted where possible, never in browser, restricted and audited.', ['server', 'encrypted', 'never', 'browser', 'audit'], true),
        q('backup', 'Backup and restore', 'How should backups be managed?', 'Encrypted backups, tested restore, restricted access, retention policy and audit logs.', ['encrypted', 'restore', 'restricted', 'retention', 'audit'], true),
        q('incident', 'Security incident', 'What happens after suspected data exposure?', 'Contain, preserve logs, notify responsible lead, assess breach, document and follow incident process.', ['contain', 'logs', 'notify', 'breach', 'document'], true),
      ],
    };
  }
  if (normalized.includes('hr') || normalized.includes('recruitment')) {
    return {
      title: `${role} AI Safe Recruitment Exam`,
      durationMinutes: 60,
      passMark: 80,
      maxCriticalErrors: 0,
      rules: ['Right-to-work must be checked before work starts.', 'DBS eligibility must be role assessed.', 'AI recommendation is advisory only.'],
      questions: [
        q('right-to-work', 'Right to work', 'When must right-to-work evidence be checked and recorded?', 'Before employment starts, using valid evidence and retaining the check record.', ['before', 'valid', 'evidence', 'record'], true),
        q('dbs', 'DBS', 'How do you decide the right DBS level for a care role?', 'Assess duties against eligibility/regulated activity guidance, document rationale and request the correct check.', ['duties', 'eligibility', 'regulated', 'document', 'check'], true),
        q('ai-fairness', 'AI fairness', 'How should AI CV screening be used safely?', 'As a draft only, avoid protected characteristic assumptions, require human review and record final reasons.', ['draft', 'protected', 'human', 'review', 'reasons'], true),
      ],
    };
  }
  return {
    title: `${role || 'Care Candidate'} AI Care Safety Exam`,
    durationMinutes: 45,
    passMark: 75,
    maxCriticalErrors: 0,
    rules: ['Answer all questions.', 'Any critical safeguarding, medication or moving-handling failure requires human review.', 'AI recommendation is advisory only.'],
    questions: [
      q('safeguarding', 'Safeguarding', 'A client tells you a relative is taking money from them. What do you do?', 'Listen calmly, do not promise secrecy, record facts, report to safeguarding lead/office and protect the client.', ['listen', 'record', 'report', 'safeguarding', 'protect'], true),
      q('medication', 'Medication safety', 'A medication dose appears to have been missed. What is the safe response?', 'Do not give extra medication without instruction, check MAR/eMAR, contact senior/manager, record and follow policy.', ['do not', 'emar', 'manager', 'record', 'policy'], true),
      q('moving', 'Moving and handling', 'The care plan says hoist and two carers, but the client asks you to lift manually. What do you do?', 'Do not manually lift, follow care plan/risk assessment, call office and only use equipment if trained.', ['do not lift', 'care plan', 'risk', 'call', 'trained'], true),
      q('dignity', 'Dignity and consent', 'How do you support personal care while preserving dignity?', 'Ask consent, explain each step, offer choices, protect privacy and encourage independence.', ['consent', 'explain', 'choice', 'privacy', 'independence'], false),
      q('records', 'Care records', 'What makes a good care note?', 'Factual, dated, timed, respectful, objective, includes tasks, changes, concerns and actions.', ['factual', 'dated', 'timed', 'objective', 'concerns'], false),
    ],
  };
}

function scoreQuestion(question: ExamQuestion) {
  const answer = question.candidateAnswer.toLowerCase();
  if (!answer.trim()) return { score: 0, missing: question.keywords };
  const matched = question.keywords.filter((keyword) => answer.includes(keyword.toLowerCase()));
  const lengthBoost = answer.length > 140 ? 1 : answer.length > 80 ? 0.5 : 0;
  const score = Math.max(0, Math.min(question.maxScore, Math.round((matched.length / Math.max(1, question.keywords.length)) * question.maxScore + lengthBoost)));
  return { score, missing: question.keywords.filter((keyword) => !matched.includes(keyword)) };
}

function createProfileDraft(candidate: CandidatePortalRecord | null): CandidateApplicationProfile {
  const profile = candidate?.applicationProfile || {};
  return {
    address: profile.address || candidate?.address || '',
    postcode: profile.postcode || candidate?.postcode || '',
    dateOfBirth: profile.dateOfBirth || candidate?.dateOfBirth || '',
    nationalInsuranceNumber: profile.nationalInsuranceNumber || candidate?.nationalInsuranceNumber || '',
    desiredHours: profile.desiredHours || candidate?.desiredHours || '',
    preferredStartDate: profile.preferredStartDate || candidate?.preferredStartDate || '',
    hasDrivingLicence: Boolean(profile.hasDrivingLicence || candidate?.hasDrivingLicence),
    hasOwnTransport: Boolean(profile.hasOwnTransport || candidate?.hasOwnTransport),
    emergencyContactName: profile.emergencyContactName || '',
    emergencyContactPhone: profile.emergencyContactPhone || '',
    referenceOneName: profile.referenceOneName || '',
    referenceOneEmail: profile.referenceOneEmail || '',
    referenceTwoName: profile.referenceTwoName || '',
    referenceTwoEmail: profile.referenceTwoEmail || '',
    rightToWorkDeclaration: Boolean(profile.rightToWorkDeclaration),
    dbsConsent: Boolean(profile.dbsConsent),
    gdprConsent: Boolean(profile.gdprConsent),
    truthfulDeclaration: Boolean(profile.truthfulDeclaration),
  };
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsText(file);
  });
}

export const CandidateExamPortal: React.FC = () => {
  const { token, user, logout } = useAuth();
  const company = useCompanyProfile();
  const [candidate, setCandidate] = useState<CandidatePortalRecord | null>(null);
  const [profileDraft, setProfileDraft] = useState<CandidateApplicationProfile>(() => createProfileDraft(null));
  const [documents, setDocuments] = useState<CandidateDocument[]>([]);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [template, setTemplate] = useState<ExamTemplate | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startedAt, setStartedAt] = useState('');
  const [candidateDeclaration, setCandidateDeclaration] = useState('');
  const [aiNarrative, setAiNarrative] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [resultSummary, setResultSummary] = useState('');

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/hr/candidate-portal/me', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || 'Candidate exam access is not available.');
        const nextCandidate = payload.data.candidate as CandidatePortalRecord;
        const nextDocuments = Array.isArray(payload.data.documents) ? payload.data.documents as CandidateDocument[] : [];
        let nextTemplate = baseExamForRole(nextCandidate.roleAppliedFor);
        let generatedByBackendAi = false;
        try {
          const examResponse = await fetch('/api/hr/candidate-portal/generate-exam', {
            method: 'POST',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ roleAppliedFor: nextCandidate.roleAppliedFor }),
          });
          const examPayload = await examResponse.json().catch(() => ({}));
          if (examResponse.ok && examPayload?.data?.exam?.questions?.length) {
            nextTemplate = examPayload.data.exam as ExamTemplate;
            generatedByBackendAi = Boolean(examPayload.data.configured);
          }
        } catch {
          generatedByBackendAi = false;
        }
        const hasCandidateQuestion = nextTemplate.questions.some((question) => question.id === 'candidate-specific');
        const personalisedQuestions = hasCandidateQuestion ? nextTemplate.questions : [
          ...nextTemplate.questions,
          q(
            'candidate-specific',
            'Candidate-specific evidence',
            `Using your own experience, explain why you are suitable for the ${nextCandidate.roleAppliedFor} role at ${company.name}.`,
            'The answer should connect experience to safe practice, policy, confidentiality, escalation and willingness to learn.',
            ['experience', 'safe', 'policy', 'confidential', 'learn'],
            false
          ),
        ];
        setCandidate(nextCandidate);
        setProfileDraft(createProfileDraft(nextCandidate));
        setDocuments(nextDocuments);
        setTemplate({ ...nextTemplate, questions: personalisedQuestions });
        setQuestions(personalisedQuestions);
        setStartedAt(new Date().toISOString());
        setAiNarrative(`${generatedByBackendAi ? 'Backend AI generated' : 'Safe fallback generated'} the exam for ${nextCandidate.fullName}. The assistant will score evidence against the role requirements and save the result for HR review. Final hiring decisions remain human.`);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not load candidate exam.');
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [company.name, token]);

  const score = useMemo(() => questions.reduce((sum, question) => sum + Number(question.score || 0), 0), [questions]);
  const maxScore = useMemo(() => questions.reduce((sum, question) => sum + Number(question.maxScore || 5), 0), [questions]);
  const percentage = maxScore ? Math.round((score / maxScore) * 100) : 0;
  const criticalErrors = questions.filter((question) => question.critical && Number(question.score || 0) < 3).length;
  const currentQuestion = questions[currentIndex];

  const updateAnswer = (value: string) => {
    setQuestions((items) => items.map((item, index) => index === currentIndex ? { ...item, candidateAnswer: value } : item));
  };

  const updateProfile = (patch: Partial<CandidateApplicationProfile>) => {
    setProfileDraft((current) => ({ ...current, ...patch }));
  };

  const saveProfile = async () => {
    if (!candidate) return;
    setIsSavingProfile(true);
    try {
      const response = await fetch('/api/hr/candidate-portal/profile', {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: candidate.phone,
          roleAppliedFor: candidate.roleAppliedFor,
          availability: candidate.availability,
          experienceSummary: candidate.experienceSummary,
          address: profileDraft.address,
          postcode: profileDraft.postcode,
          dateOfBirth: profileDraft.dateOfBirth,
          nationalInsuranceNumber: profileDraft.nationalInsuranceNumber,
          desiredHours: profileDraft.desiredHours,
          preferredStartDate: profileDraft.preferredStartDate,
          hasDrivingLicence: profileDraft.hasDrivingLicence,
          hasOwnTransport: profileDraft.hasOwnTransport,
          applicationProfile: profileDraft,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not save your application details.');
      setCandidate(payload.data.candidate);
      setProfileDraft(createProfileDraft(payload.data.candidate));
      toast.success('Application details saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save your application details.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const uploadDocument = async (file: File | null, documentType = 'CV') => {
    if (!file) return;
    setIsUploading(true);
    try {
      const contentText = await readFileAsText(file);
      const response = await fetch('/api/hr/candidate-portal/upload-document', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ documentType, fileName: file.name, contentText }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not upload document.');
      setCandidate(payload.data.candidate);
      setDocuments((current) => [payload.data.candidateDocument, ...current]);
      toast.success(`${documentType} uploaded and saved.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  const submitApplication = async () => {
    setIsSubmittingApplication(true);
    try {
      await saveProfile();
      const response = await fetch('/api/hr/candidate-portal/submit-application', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not submit application.');
      setCandidate(payload.data.candidate);
      toast.success('Application submitted. You can now complete the AI exam.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not submit application.');
    } finally {
      setIsSubmittingApplication(false);
    }
  };

  const aiReviewCurrent = () => {
    if (!currentQuestion) return;
    const result = scoreQuestion(currentQuestion);
    setQuestions((items) => items.map((item, index) => index === currentIndex ? {
      ...item,
      score: result.score,
      notes: result.missing.length
        ? `AI review: answer needs more evidence for ${result.missing.join(', ')}.`
        : 'AI review: key evidence covered. HR will still review the result.',
    } : item));
    setAiNarrative(result.missing.length
      ? `Please improve the answer by explaining: ${result.missing.join(', ')}.`
      : 'This answer has enough key evidence for AI scoring. You may continue.');
  };

  const submitExam = async () => {
    if (!template || !candidate) return;
    const unanswered = questions.filter((question) => !question.candidateAnswer.trim()).length;
    if (unanswered) {
      toast.error(`Please answer all questions before submitting. Missing: ${unanswered}.`);
      return;
    }

    const assessed = questions.map((question) => {
      const result = scoreQuestion(question);
      return {
        ...question,
        score: result.score,
        notes: result.missing.length ? `AI final review: missing ${result.missing.join(', ')}.` : 'AI final review: key evidence covered.',
      };
    });
    const finalScore = assessed.reduce((sum, question) => sum + Number(question.score || 0), 0);
    const finalMax = assessed.reduce((sum, question) => sum + Number(question.maxScore || 5), 0);
    const finalPercentage = Math.round((finalScore / Math.max(1, finalMax)) * 100);
    const finalCriticalErrors = assessed.filter((question) => question.critical && Number(question.score || 0) < 3).length;
    const finalNarrative = [
      `AI completed the candidate exam for ${candidate.fullName}.`,
      `Score: ${finalScore}/${finalMax} (${finalPercentage}%).`,
      `Critical errors: ${finalCriticalErrors}/${template.maxCriticalErrors}.`,
      finalPercentage >= template.passMark && finalCriticalErrors <= template.maxCriticalErrors
        ? 'Advisory result: may progress to HR/manager review.'
        : 'Advisory result: HR/manager review required before any progression.',
    ].join(' ');

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/hr/candidate-portal/submit-exam', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          examTemplate: template.title,
          passMark: template.passMark,
          maxCriticalErrors: template.maxCriticalErrors,
          questions: assessed,
          aiNarrative: finalNarrative,
          rulesSummary: template.rules.join(' '),
          candidateDeclaration,
          finalRecommendation: `${finalNarrative} Final hiring decision must be made by HR/Registered Manager.`,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not submit exam.');
      setQuestions(assessed);
      setAiNarrative(finalNarrative);
      setCompleted(true);
      const outcome = payload?.data?.outcome || (finalPercentage >= template.passMark && finalCriticalErrors <= template.maxCriticalErrors ? 'APPROVED' : 'REJECTED');
      setResultSummary(`${outcome}: score ${payload?.data?.score || `${finalScore}/${finalMax}`} (${payload?.data?.percentage ?? finalPercentage}%). Your result has been saved to your candidate file.`);
      toast.success(`Exam submitted. Result: ${outcome}. Your temporary login has now been closed.`);
      window.setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 9000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not submit exam.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-700">Loading candidate exam...</div>;
  }

  if (!candidate || !template) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 p-6">
        <div className="max-w-lg rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-10 w-10 text-red-600" />
          <h1 className="mt-4 text-2xl font-bold text-slate-950">Exam access unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">This login may have expired, already been used, or may not be a candidate exam account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-purple-800">{company.name}</p>
            <h1 className="text-2xl font-bold text-slate-950">Candidate AI Exam Portal</h1>
            <p className="mt-1 text-sm text-slate-600">Signed in as {user?.name || candidate.fullName}. This temporary login closes automatically after submission.</p>
          </div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <LogOut className="h-4 w-4" />
            Leave exam
          </button>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-5 py-6 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Candidate</p>
            <h2 className="mt-2 text-xl font-bold text-slate-950">{candidate.fullName}</h2>
            <p className="mt-1 text-sm text-slate-600">{candidate.roleAppliedFor}</p>
            <p className="mt-3 text-xs text-slate-500">{candidate.email}</p>
          </div>
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-5">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800"><Clock3 className="h-4 w-4" /> Exam rules</p>
            <h3 className="mt-2 font-bold text-slate-950">{template.title}</h3>
            <p className="mt-2 text-sm text-slate-700">Duration: {template.durationMinutes} minutes. Pass mark: {template.passMark}%.</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {template.rules.map((rule) => <li key={rule}>- {rule}</li>)}
            </ul>
            <p className="mt-3 text-xs text-slate-500">Started: {startedAt ? new Date(startedAt).toLocaleString() : '-'}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Progress</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{currentIndex + 1}/{questions.length}</p>
            <p className="mt-1 text-sm text-slate-600">Score so far: {score}/{maxScore} ({percentage}%)</p>
            <p className={`mt-2 text-sm font-semibold ${criticalErrors ? 'text-red-700' : 'text-emerald-700'}`}>Critical errors: {criticalErrors}/{template.maxCriticalErrors}</p>
          </div>
        </aside>

        <section className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800"><ClipboardCheck className="h-4 w-4" /> Candidate application</p>
                <h2 className="mt-2 text-xl font-bold text-slate-950">Complete your application details</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">Save your details, upload your CV, submit the application, then complete the AI exam. HR will review the full record.</p>
              </div>
              <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800">{candidate.applicationSubmittedAt ? 'APPLICATION SUBMITTED' : 'IN PROGRESS'}</span>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone</span>
                <input value={candidate.phone || ''} onChange={(event) => setCandidate({ ...candidate, phone: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role applied for</span>
                <input value={candidate.roleAppliedFor || ''} onChange={(event) => setCandidate({ ...candidate, roleAppliedFor: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address</span>
                <input value={profileDraft.address || ''} onChange={(event) => updateProfile({ address: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Postcode</span>
                <input value={profileDraft.postcode || ''} onChange={(event) => updateProfile({ postcode: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date of birth</span>
                <input type="date" value={profileDraft.dateOfBirth || ''} onChange={(event) => updateProfile({ dateOfBirth: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">National Insurance number</span>
                <input value={profileDraft.nationalInsuranceNumber || ''} onChange={(event) => updateProfile({ nationalInsuranceNumber: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Desired hours</span>
                <input value={profileDraft.desiredHours || ''} onChange={(event) => updateProfile({ desiredHours: event.target.value })} placeholder="Example: 30 hours/week" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preferred start date</span>
                <input type="date" value={profileDraft.preferredStartDate || ''} onChange={(event) => updateProfile({ preferredStartDate: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Availability</span>
                <input value={candidate.availability || ''} onChange={(event) => setCandidate({ ...candidate, availability: event.target.value })} placeholder="Example: Monday-Friday mornings, alternate weekends" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" disabled={completed} />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Experience summary</span>
                <textarea value={candidate.experienceSummary || ''} onChange={(event) => setCandidate({ ...candidate, experienceSummary: event.target.value })} rows={4} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" disabled={completed} />
              </label>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {[
                ['Driving licence', 'hasDrivingLicence'],
                ['Own transport', 'hasOwnTransport'],
                ['I confirm I can provide right-to-work evidence', 'rightToWorkDeclaration'],
                ['I consent to DBS checks required for the role', 'dbsConsent'],
                ['I consent to recruitment data processing', 'gdprConsent'],
                ['I confirm my application information is true', 'truthfulDeclaration'],
              ].map(([label, key]) => (
                <label key={key} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(profileDraft[key as keyof CandidateApplicationProfile])}
                    onChange={(event) => updateProfile({ [key]: event.target.checked } as Partial<CandidateApplicationProfile>)}
                    className="h-4 w-4 accent-purple-700"
                    disabled={completed}
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={() => void saveProfile()} disabled={isSavingProfile || completed} className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50 disabled:opacity-60">
                <Save className="h-4 w-4" />
                {isSavingProfile ? 'Saving...' : 'Save application details'}
              </button>
              <button type="button" onClick={() => void submitApplication()} disabled={isSubmittingApplication || completed} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                <Send className="h-4 w-4" />
                {isSubmittingApplication ? 'Submitting...' : 'Submit application'}
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800"><FileText className="h-4 w-4" /> Documents</p>
                <h2 className="mt-2 text-xl font-bold text-slate-950">Upload your CV and evidence</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">Documents are attached to your recruitment file for HR review.</p>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                <Upload className="h-4 w-4" />
                {isUploading ? 'Uploading...' : 'Upload CV'}
                <input type="file" accept=".txt,.md,.csv,.doc,.docx,.pdf" className="hidden" disabled={isUploading || completed} onChange={(event) => void uploadDocument(event.target.files?.[0] || null, 'CV')} />
              </label>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {documents.length ? documents.map((document) => (
                <div key={document.id} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <p className="font-semibold text-slate-950">{document.documentType}</p>
                  <p className="mt-1 text-sm text-slate-600">{document.fileName || 'Document uploaded'}</p>
                  <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">{document.status}</span>
                </div>
              )) : <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">No documents uploaded yet. Upload a CV before submitting the application.</p>}
            </div>
          </div>

          {resultSummary && (
            <div className={`rounded-lg border p-5 shadow-sm ${resultSummary.startsWith('APPROVED') ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
              <p className={`flex items-center gap-2 text-sm font-bold ${resultSummary.startsWith('APPROVED') ? 'text-emerald-900' : 'text-red-900'}`}>
                <Award className="h-5 w-5" />
                Candidate exam result
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-700">{resultSummary} This is saved in your candidate file. Final employment decision remains with HR/Registered Manager.</p>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">{currentQuestion?.category}</p>
                <h2 className="mt-2 text-xl font-bold leading-7 text-slate-950">{currentQuestion?.question}</h2>
                {currentQuestion?.critical && <p className="mt-2 inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">Critical question</p>}
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Max {currentQuestion?.maxScore || 5} points</span>
            </div>
            <label className="mt-5 block space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your answer</span>
              <textarea
                value={currentQuestion?.candidateAnswer || ''}
                onChange={(event) => updateAnswer(event.target.value)}
                rows={8}
                disabled={completed}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm leading-6 text-slate-800 focus:border-purple-500 focus:outline-none"
                placeholder="Write your answer clearly. Include what you would do, who you would inform, how you would record it, and how you would keep the person safe."
              />
            </label>
            {currentQuestion?.notes && <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900">{currentQuestion.notes}</p>}
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={aiReviewCurrent} disabled={completed} className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50 disabled:opacity-50">
                <Bot className="h-4 w-4" />
                Ask AI to review this answer
              </button>
              <button type="button" onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))} disabled={currentIndex === 0} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Previous</button>
              <button type="button" onClick={() => setCurrentIndex((index) => Math.min(questions.length - 1, index + 1))} disabled={currentIndex >= questions.length - 1} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Next</button>
            </div>
          </div>

          <div className="rounded-lg border border-purple-200 bg-white p-5 shadow-sm">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800"><Sparkles className="h-4 w-4" /> AI assistant</p>
            <p className="mt-2 text-sm leading-6 text-slate-700">{aiNarrative}</p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={Boolean(candidateDeclaration)}
                onChange={(event) => setCandidateDeclaration(event.target.checked ? 'I confirm these answers are my own and true to the best of my knowledge.' : '')}
                className="mt-1 h-5 w-5 accent-purple-700"
                disabled={completed}
              />
              <span>
                <span className="block font-semibold text-slate-950">Candidate declaration</span>
                <span className="text-sm text-slate-600">I confirm these answers are my own and true to the best of my knowledge.</span>
              </span>
            </label>
            <button
              type="button"
              onClick={() => void submitExam()}
              disabled={isSubmitting || completed || !candidateDeclaration}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-purple-700 px-5 py-3 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
            >
              {completed ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              {completed ? 'Exam submitted' : isSubmitting ? 'Submitting exam...' : 'Submit final exam'}
            </button>
            <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4" />
              After submission, the result is saved to your candidate file and this temporary login is removed.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};
